import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getUserType } from "util/auth";
import MenuPriceSetting from "components/menu/MenuPriceSetting";
import ButtonComponent from "components/button/ButtonComponent";
import SearchBox from "components/SearchBox";
import { isEqual } from "lodash";
import useLoadMore from "hooks/useLoadMore";
import FourBundlePriceTable from "../components/FourBundlePriceTable";
import use4BundlePriceTwset from "../hooks/use4BundlePriceTwset";

/**
 * FourBundlePriceList Component
 *
 * Displays and manages the 4-bundle special price settings for tire products.
 * Allows users to search, filter, and update special prices by brand, item code, and flags.
 * Provides download of the latest CSV data and supports infinite scroll for loading more prices.
 * @returns {Element} The rendered 4-bundle price list page.
 */
function FourBundlePriceList() {
  const navigate = useNavigate();
  const {
    prices,
    setPrices,
    setTempQuery,
    tempQuery,
    setQuery,
    query,
    updatePrice,
    csvDownload,
    onLoadMore,
    loading,
  } = use4BundlePriceTwset();
  const { renderNodeLoader } = useLoadMore(onLoadMore, loading);

  const handleSubmitSearch = () => {
    if (
      !isEqual(tempQuery, {
        brand_cd: query.brand_cd,
        item_cd: query.item_cd,
        honbu_flag: query.honbu_flag,
        store_flag: query.store_flag,
      })
    ) {
      setQuery((prev) => ({ ...prev, ...tempQuery }));
    }
  };

  const handleDownloadClick = () => {
    const link = document.createElement("a");
    link.href = csvDownload.mst_4bundle_price_twset_url as string;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Box sx={{ px: { xs: 2, md: 2.5, lg: 4, xl: 7 }, pt: { xs: 2, md: 2.5, lg: 3 } }}>
        <Button onClick={() => navigate("/")} variant="custom-outlined">
          <Box>＜ 戻る</Box>
        </Button>
      </Box>
      <Box
        sx={{ display: "flex", gap: 6, flexWrap: "wrap", padding: { xs: 2, md: 2.5, lg: 4, xl: 7 } }}
      >
      <Box
        sx={{
          display: { xs: "flex", xl: "block" },
          justifyContent: "space-between",
          flexBasis: { xs: "100%", xl: "290px" },
        }}
      >
        <Typography
          variant="h5"
          margin={0}
          textAlign="left"
          fontSize={{ xs: 28, lg: 33 }}
          marginBottom="50px"
          color="var(--gray-700)"
        >
          店舗別 <br />
          各種設定
        </Typography>
        <MenuPriceSetting />
      </Box>
      <Box sx={{ width: { xs: "100%", xl: "auto" } }}>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", gap: 3, flexWrap: "wrap", mb: 3 }}
        >
          <Box>
            <Typography
              sx={{ fontSize: { xs: 18, xl: 25 } }}
              variant="h5"
              color="var(--gray-700)"
              fontWeight="medium"
            >
              タイヤ特価設定
            </Typography>
            <Typography
              sx={{ fontSize: { xs: 20, md: 24, xl: 35 } }}
              variant="h2"
              color="var(--black)"
              fontWeight="regular"
            >
              タイヤ商材の4本特価を確認できます
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              mr: 5,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontSize: { xs: 18, md: 22 } }}
              color="var(--black)"
              fontWeight="medium"
            >
              最新本部特価データ:
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontSize: { xs: 18, md: 22 } }}
              color="var(--black)"
              fontWeight="medium"
            >
              {csvDownload.mst_4bundle_price_twset_latest_date}
            </Typography>
          </Box>
        </Box>
        <hr />
        <Box sx={{ margin: { xs: "32px 0", xl: "32px 0" } }}>
          <Typography
            sx={{ fontSize: { xs: 20, md: 24, xl: 35 } }}
            variant="h2"
            color="var(--black)"
            fontWeight="regular"
          >
            タイヤ商材の4本特価を独自設定（上書き）します
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: "5px",
              alignItems: "center",
              mt: 5,
              flexWrap: "wrap",
            }}
          >
            <SearchBox
              maxLength={12}
              onChangeText={(value) => setTempQuery((prev) => ({ ...prev, item_cd: value.trim() }))}
              title="YH商品コード"
              id="YH商品コード"
              placeholder="YH商品コード"
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
            />
            <SearchBox
              onChangeText={(value) =>
                setTempQuery((prev) => ({ ...prev, brand_cd: value.trim() }))
              }
              title="ブランドコード"
              id="ブランドコード"
              placeholder="ブランドコード"
              sx={{
                width: {
                  xs: "100%",
                  md: "calc((100% - 15px) / 2)",
                  xl: "calc((100% - 30px) / 3)",
                },
              }}
            />
            <Box>
              <FormControlLabel
                sx={{
                  fontSize: { xs: 16, md: 20, lg: 23 },
                  color: "var(--black)",
                }}
                control={
                  <Checkbox
                    checked={!!tempQuery.honbu_flag}
                    color="secondary"
                    onChange={(e: any) => {
                      setTempQuery((prev) => ({ ...prev, honbu_flag: e.target?.checked }));
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: 16, md: 20, lg: 23 }, color: "var(--black)" }}>
                    本部設定品を表示
                  </Typography>
                }
              />
              <FormControlLabel
                sx={{
                  fontSize: { xs: 16, md: 20, lg: 23 },
                  color: "var(--black)",
                }}
                control={
                  <Checkbox
                    checked={!!tempQuery.store_flag}
                    color="secondary"
                    onChange={(e: any) => {
                      setTempQuery((prev) => ({ ...prev, store_flag: e.target?.checked }));
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: { xs: 16, md: 20, lg: 23 }, color: "var(--black)" }}>
                    店舗設定品を表示
                  </Typography>
                }
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <ButtonComponent
              onClick={handleSubmitSearch}
              theme="dark"
              label="検索"
              sx={{ width: "184px", mt: 0 }}
            />
          </Box>
        </Box>
        <Box sx={{ width: { xs: "100%", lg: "auto", ml: { xs: 3, lg: 0 } } }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 7, mb: 1.5 }}>
            <Typography
              variant="h6"
              sx={{
                color: "var(--black)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
                marginRight: "20px",
              }}
            >
              {getUserType() === "C0002" ? "黒字：本部/企業設定" : "黒字：本部設定"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "var(--red-800)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
              }}
            >
              {getUserType() === "C0006" ? "赤字：企業上書設定" : "赤字：店舗設定"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ overflowX: "auto", width: "100%", pb: 3 }}>
          <FourBundlePriceTable prices={prices} setPrices={setPrices} onUpdate={updatePrice} />
        </Box>
        {renderNodeLoader()}
      </Box>
    </Box>
    </>
  );
}

export default FourBundlePriceList;
